import React, { useMemo } from 'react'
import { Alert, Button, Form } from 'react-bootstrap'
import { useTranslation } from 'react-i18next'
import { InputPermissionsGroup } from '../common/InputPermissionsGroup'
import { InputPermissionsUser } from '../common/InputPermissionsUser'
import {
  WorkflowAction,
  WorkflowAttributePermissionRule,
  WorkflowDocumentActionPermissionRule,
} from '../../types'

interface WorkflowPermissionActionFieldsProps {
  formData: WorkflowAction
  documentTypes: any[]
  customFields: any[]
  users: any[]
  groups: any[]
  onChange: (field: keyof WorkflowAction, value: any) => void
}

const DOCUMENT_ACTIONS = [
  'download',
  'new_version',
  'reprocess',
  'refresh_template',
  'print',
  'more_like_this',
  'pdf_editor',
  'share_link',
  'email',
  'add_attribute',
  'delete',
]

const fieldId = (field: any) => Number(field?.id)

export const WorkflowPermissionActionFields: React.FC<WorkflowPermissionActionFieldsProps> = ({
  formData,
  documentTypes,
  customFields,
  users,
  groups,
  onChange,
}) => {
  const { t } = useTranslation()
  const selectedDocumentType = documentTypes.find((item) => item.id === formData.permission_document_type)

  const typeFields = useMemo(() => {
    const ids = selectedDocumentType?.custom_fields || selectedDocumentType?.custom_field_configs?.map((item: any) => item.custom_field) || []
    const configs = new Map((selectedDocumentType?.custom_field_configs || []).map((item: any) => [item.custom_field, item]))
    return ids
      .map((id: number) => {
        const field = customFields.find((item) => item.id === id)
        return field ? { field, config: configs.get(id) } : null
      })
      .filter(Boolean) as Array<{ field: any; config?: any }>
  }, [customFields, selectedDocumentType])

  const attributeRules = formData.permission_attribute_rules || []
  const actionRules = formData.permission_action_rules || []
  const userOptions = users.length > 0 ? users : undefined
  const groupOptions = groups.length > 0 ? groups : undefined
  const menuPortalZIndex = 2300
  const systemAttributes = useMemo(
    () => [
      { id: 'title', label: t('title') },
      { id: 'archive_serial_number', label: t('asn') },
      { id: 'created', label: t('created') },
      { id: 'correspondent', label: t('correspondent') },
      { id: 'document_type', label: t('document_type') },
      { id: 'storage_path', label: t('storage_path') },
      { id: 'tags', label: t('tags') },
    ],
    [t],
  )

  const getAttributeRule = (id: number): WorkflowAttributePermissionRule => {
    const existing = attributeRules.find((rule) => rule.field_id === id)
    const config = typeFields.find((item) => fieldId(item.field) === id)?.config
    return existing || {
      field_id: id,
      visible: true,
      editable: true,
      required: Boolean(config?.required),
    }
  }

  const updateAttributeRule = (rule: WorkflowAttributePermissionRule) => {
    const next = attributeRules.filter((item) => item.field_id !== rule.field_id)
    onChange('permission_attribute_rules', [...next, rule])
  }

  const getSystemAttributeRule = (systemField: string): WorkflowAttributePermissionRule =>
    attributeRules.find((rule) => rule.system_field === systemField) || {
      system_field: systemField,
      visible: true,
      editable: true,
      required: false,
    }

  const updateSystemAttributeRule = (rule: WorkflowAttributePermissionRule) => {
    const next = attributeRules.filter((item) => item.system_field !== rule.system_field)
    onChange('permission_attribute_rules', [...next, rule])
  }

  const updateActionRule = (index: number, rule: WorkflowDocumentActionPermissionRule) => {
    const next = [...actionRules]
    next[index] = rule
    onChange('permission_action_rules', next)
  }

  const actionLabel = (action: string) => t(`workflow_permission_action_${action}`, { defaultValue: action })

  return (
    <div className="border rounded p-3 mb-3">
      <Alert variant="warning" className="mb-4">
        <strong>{t('workflow_permission_warning_title')}</strong>
        <div>{t('workflow_permission_warning_text')}</div>
      </Alert>

      <h5>{t('workflow_permission_attributes')}</h5>
      <p className="text-muted small">{t('workflow_permission_attributes_help')}</p>
      <Form.Group className="mb-3">
        <Form.Label>{t('workflow_permission_document_type')}</Form.Label>
        <Form.Select
          value={formData.permission_document_type || ''}
          onChange={(event) => {
            const value = event.target.value ? Number(event.target.value) : null
            onChange('permission_document_type', value)
            // System attribute permissions are independent of document type.
            // Changing the type only resets custom-field rules.
            onChange('permission_attribute_rules', attributeRules.filter((rule) => rule.system_field))
          }}
        >
          <option value="">{t('workflow_permission_all_document_types')}</option>
          {documentTypes.map((type) => (
            <option key={type.id} value={type.id}>
              {type.name}
            </option>
          ))}
        </Form.Select>
      </Form.Group>

      <Form.Label>{t('workflow_permission_applies_to')}</Form.Label>
      <div className="row mb-4">
        <div className="col-md-6">
          <small className="text-muted d-block mb-1">{t('users')}</small>
          <InputPermissionsUser value={formData.permission_users || []} users={userOptions} menuPortalZIndex={menuPortalZIndex} onChange={(value) => onChange('permission_users', value)} />
        </div>
        <div className="col-md-6">
          <small className="text-muted d-block mb-1">{t('groups')}</small>
          <InputPermissionsGroup value={formData.permission_groups || []} groups={groupOptions} menuPortalZIndex={menuPortalZIndex} onChange={(value) => onChange('permission_groups', value)} />
        </div>
      </div>

      <h6>{t('workflow_permission_system_attributes')}</h6>
      <div className="table-responsive mb-4">
        <table className="table table-sm align-middle">
          <thead>
            <tr>
              <th>{t('workflow_permission_attribute')}</th>
              <th className="text-center">{t('workflow_permission_visible')}</th>
              <th className="text-center">{t('workflow_permission_editable')}</th>
              <th className="text-center">{t('workflow_permission_required')}</th>
            </tr>
          </thead>
          <tbody>
            {systemAttributes.map(({ id, label }) => {
              const rule = getSystemAttributeRule(id)
              return (
                <tr key={id}>
                  <td>{label}</td>
                  {(['visible', 'editable', 'required'] as const).map((permission) => (
                    <td className="text-center" key={permission}>
                      <Form.Check
                        type="checkbox"
                        checked={rule[permission]}
                        onChange={(event) => updateSystemAttributeRule({ ...rule, [permission]: event.target.checked })}
                        aria-label={`${label}: ${t(`workflow_permission_${permission}`)}`}
                      />
                    </td>
                  ))}
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>

      {selectedDocumentType && typeFields.length === 0 && <p className="text-muted">{t('workflow_permission_no_attributes')}</p>}
      {selectedDocumentType && typeFields.length > 0 && (
        <div className="table-responsive mb-4">
          <table className="table table-sm align-middle">
            <thead>
              <tr>
                <th>{t('workflow_permission_attribute')}</th>
                <th className="text-center">{t('workflow_permission_visible')}</th>
                <th className="text-center">{t('workflow_permission_editable')}</th>
                <th className="text-center">{t('workflow_permission_required')}</th>
              </tr>
            </thead>
            <tbody>
              {typeFields.map(({ field }) => {
                const rule = getAttributeRule(field.id)
                return (
                  <tr key={field.id}>
                    <td>{field.name}</td>
                    {(['visible', 'editable', 'required'] as const).map((permission) => (
                      <td className="text-center" key={permission}>
                        <Form.Check
                          type="checkbox"
                          checked={rule[permission]}
                          onChange={(event) => updateAttributeRule({ ...rule, [permission]: event.target.checked })}
                          aria-label={`${field.name}: ${t(`workflow_permission_${permission}`)}`}
                        />
                      </td>
                    ))}
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}

      <hr />
      <h5>{t('workflow_permission_actions')}</h5>
      <p className="text-muted small">{t('workflow_permission_actions_help')}</p>
      {actionRules.map((rule, index) => (
        <div className="border rounded p-3 mb-3" key={`permission-rule-${index}`}>
          <div className="d-flex justify-content-between align-items-center mb-3">
            <Form.Select
              className="w-auto"
              value={rule.target_type}
              onChange={(event) => updateActionRule(index, { ...rule, target_type: event.target.value as 'user' | 'group', target_ids: [] })}
            >
              <option value="group">{t('workflow_permission_target_group')}</option>
              <option value="user">{t('workflow_permission_target_user')}</option>
            </Form.Select>
            <Button variant="outline-danger" size="sm" onClick={() => onChange('permission_action_rules', actionRules.filter((_, itemIndex) => itemIndex !== index))}>
              <i className="bi bi-trash me-1" />
              {t('workflow_permission_delete_row')}
            </Button>
          </div>
          {rule.target_type === 'group' ? (
            <InputPermissionsGroup
              value={rule.target_ids}
              groups={groupOptions}
              menuPortalZIndex={menuPortalZIndex}
              onChange={(value) => updateActionRule(index, { ...rule, target_ids: value })}
            />
          ) : (
            <InputPermissionsUser
              value={rule.target_ids}
              users={userOptions}
              menuPortalZIndex={menuPortalZIndex}
              onChange={(value) => updateActionRule(index, { ...rule, target_ids: value })}
            />
          )}
          <div className="row mt-3">
            {DOCUMENT_ACTIONS.map((action) => (
              <div className="col-md-4 mb-2" key={action}>
                <Form.Check
                  type="checkbox"
                  label={actionLabel(action)}
                  checked={rule.actions.includes(action)}
                  onChange={(event) => {
                    const actions = event.target.checked ? [...rule.actions, action] : rule.actions.filter((item) => item !== action)
                    updateActionRule(index, { ...rule, actions })
                  }}
                />
              </div>
            ))}
          </div>
        </div>
      ))}
      <Button
        variant="outline-primary"
        size="sm"
        onClick={() => onChange('permission_action_rules', [...actionRules, { target_type: 'group', target_ids: [], actions: [] }])}
      >
        <i className="bi bi-plus me-1" />
        {t('workflow_permission_add_row')}
      </Button>
    </div>
  )
}
